// Pure, dependency-free pre-validation for a Dashboard Spec — mirrors the cheap,
// high-value checks the engine validator runs (cloud-agent
// internal/chat/dashboardspec). This is for fast, friendly UX in the dashboard
// editor (block B6): fail fast on obvious mistakes before a round-trip. It is
// deliberately NOT a full JSON-Schema validator — the engine (santhosh-tekuri
// against ./schema.json) is the authoritative safety net. Keep it consistent
// with providers.ts's pure-validate philosophy.

import { DASHBOARD_SCHEMA_VERSION, type DashboardSpec, type PanelType } from './types.js';

/** One structural problem, shaped like the engine's SpecError. */
export interface SpecError {
  /** JSON-pointer-ish path into the spec, e.g. "/panels/0/binding". */
  path: string;
  /** Short failing-rule tag, e.g. "required", "enum", "integrity.layout". */
  keyword: string;
  message: string;
}

export interface ValidateResult {
  ok: boolean;
  errors: SpecError[];
}

const PANEL_TYPES: ReadonlySet<string> = new Set<PanelType>([
  'kpi', 'gauge', 'table', 'bar', 'line', 'area', 'point', 'arc', 'rule', 'tick', 'rect',
]);

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function nonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

/**
 * The result-column key a measure term produces: "[Name]" for a catalog ref
 * (string form), "[as]" for an inline aggregate (object form). Returns undefined
 * for a malformed term (the engine schema is the authority that rejects it).
 */
function measureColumnKey(term: unknown): string | undefined {
  if (typeof term === 'string') return term;
  if (isObject(term) && nonEmptyString(term.as)) return `[${term.as}]`;
  return undefined;
}

/**
 * Validates the envelope + cross-reference integrity of a candidate spec. Returns
 * `{ ok, errors }`; `ok` is true only when `errors` is empty. Never throws.
 */
export function validateDashboardSpec(input: unknown): ValidateResult {
  const errors: SpecError[] = [];
  const push = (path: string, keyword: string, message: string) => errors.push({ path, keyword, message });

  if (!isObject(input)) {
    return { ok: false, errors: [{ path: '', keyword: 'type', message: 'spec must be a JSON object' }] };
  }

  if (input.schema !== DASHBOARD_SCHEMA_VERSION) {
    push('/schema', 'const', `schema must be "${DASHBOARD_SCHEMA_VERSION}"`);
  }
  if (!nonEmptyString(input.title)) {
    push('/title', 'required', 'title is required and must be a non-empty string');
  }
  if (!isObject(input.scope) || !nonEmptyString(input.scope.repoId)) {
    push('/scope', 'required', 'scope.repoId is required');
  }

  const layout = Array.isArray(input.layout) ? input.layout : [];
  if (layout.length === 0) {
    push('/layout', 'minItems', 'layout must have at least one entry');
  }
  const panels = Array.isArray(input.panels) ? input.panels : [];
  if (panels.length === 0) {
    push('/panels', 'minItems', 'panels must have at least one panel');
  }

  // Per-panel envelope checks.
  const panelIds = new Map<string, number>();
  panels.forEach((p, i) => {
    if (!isObject(p)) {
      push(`/panels/${i}`, 'type', 'panel must be an object');
      return;
    }
    if (!nonEmptyString(p.id)) {
      push(`/panels/${i}/id`, 'required', 'panel id is required');
    } else {
      if (panelIds.has(p.id)) push(`/panels/${i}/id`, 'integrity.duplicateId', `duplicate panel id "${p.id}"`);
      panelIds.set(p.id, i);
    }
    if (typeof p.type !== 'string' || !PANEL_TYPES.has(p.type)) {
      push(`/panels/${i}/type`, 'enum', `panel type "${String(p.type)}" is not a known type`);
    }
    if (!nonEmptyString(p.title)) push(`/panels/${i}/title`, 'required', 'panel title is required');
    if (!isObject(p.viz)) push(`/panels/${i}/viz`, 'required', 'panel viz is required');

    const b = p.binding;
    if (!isObject(b)) {
      push(`/panels/${i}/binding`, 'required', 'panel binding is required');
    } else {
      if (!Array.isArray(b.measures) || b.measures.length === 0) {
        push(`/panels/${i}/binding/measures`, 'minItems', 'binding.measures must be a non-empty array');
      } else {
        // Integrity 3: measure-term result keys unique within a binding (a duplicate
        // measure ref or inline-aggregate alias would collide in the compiled output).
        const seenKeys = new Set<string>();
        b.measures.forEach((m, mi) => {
          const key = measureColumnKey(m);
          if (key === undefined) return; // malformed term; the engine schema rejects it
          const norm = key.toLowerCase();
          if (seenKeys.has(norm)) {
            push(`/panels/${i}/binding/measures/${mi}`, 'integrity.duplicateMeasure',
              `measure term "${key}" duplicates an earlier term's result column in this binding`);
          }
          seenKeys.add(norm);
        });
      }
      // grain is OPTIONAL (WP3a): when present it must be a non-empty string, but an
      // omitted grain is valid and derived engine-side. A column-name grain is
      // normalized (not rejected) by the engine bind-check.
      if ('grain' in b && b.grain !== undefined && !nonEmptyString(b.grain)) {
        push(`/panels/${i}/binding/grain`, 'type', 'binding.grain, when present, must be a non-empty string (an entity localKey or "total")');
      }
    }
  });

  // Integrity 1: id <-> layout 1:1.
  const layoutIds = new Map<string, number>();
  layout.forEach((l, i) => {
    if (!isObject(l) || !nonEmptyString(l.i)) {
      push(`/layout/${i}/i`, 'required', 'layout entry needs a string id "i"');
      return;
    }
    if (layoutIds.has(l.i)) push(`/layout/${i}/i`, 'integrity.duplicateLayout', `duplicate layout entry for id "${l.i}"`);
    layoutIds.set(l.i, i);
    if (!panelIds.has(l.i)) push(`/layout/${i}/i`, 'integrity.layout', `layout references id "${l.i}" with no matching panel`);
  });
  for (const [id, i] of panelIds) {
    if (!layoutIds.has(id)) push(`/panels/${i}`, 'integrity.layout', `panel "${id}" has no layout entry`);
  }

  // Integrity 2: param references resolve.
  const paramIds = new Set<string>();
  if (Array.isArray(input.parameters)) {
    for (const p of input.parameters) {
      if (isObject(p) && nonEmptyString(p.id)) paramIds.add(p.id);
    }
  }
  panels.forEach((p, pi) => {
    if (!isObject(p)) return;
    const b = isObject(p.binding) ? p.binding : undefined;
    const filters = b && Array.isArray(b.filters) ? b.filters : [];
    filters.forEach((f, fi) => {
      if (isObject(f) && typeof f.param === 'string' && f.param.length > 0 && !paramIds.has(f.param)) {
        push(`/panels/${pi}/binding/filters/${fi}/param`, 'integrity.paramRef', `filter references undeclared parameter "${f.param}"`);
      }
    });
    const inter = isObject(p.interaction) ? p.interaction : undefined;
    const emits = inter && Array.isArray(inter.emits) ? inter.emits : [];
    emits.forEach((e, ei) => {
      if (isObject(e) && typeof e.param === 'string' && e.param.length > 0 && !paramIds.has(e.param)) {
        push(`/panels/${pi}/interaction/emits/${ei}/param`, 'integrity.paramRef', `interaction emits undeclared parameter "${e.param}"`);
      }
    });
  });

  return { ok: errors.length === 0, errors };
}

/** Convenience type guard: true when `input` passes pre-validation. */
export function isDashboardSpec(input: unknown): input is DashboardSpec {
  return validateDashboardSpec(input).ok;
}
